import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  useManagementCreateUnitActive,
  useManagementGetUnitActive,
  useManagementUpdateUnitActive
} from '@apps-common/apis/management/unit-active'
import { NonAllocatedSimSelectModal } from '@apps-common/apps/portal/pages/management/components/non-allocated-sim'
import { CardContainer } from '@apps-common/components/common/CardContainer'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, InputInfoSM, InputSM, SelectInputSM } from '@apps-common/components/elements/form'
import { VertSpacerLg } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { CID_ACCOUNT_REGEX, IPV4_VALIDATION_REGEX, NUMBER_ONLY_REGEX } from '@apps-common/config/regex'
import { useManagementUnitSelectItems } from '@apps-common/hooks/useManagementUnitSelectItems'
import { INonAllocatedSimModel } from '@apps-common/types/management/non-allocated-sim'
import { IUnitSimActiveModel } from '@apps-common/types/management/unit'

interface IProps {
  unitId?: string
  doneCallback: (unit: IUnitSimActiveModel) => void
}

export const UnitActiveDetailsManagementForm: React.FC<IProps> = ({ unitId, doneCallback }) => {
  const isCreating = unitId === undefined
  const [nonAllocatedSim, setNonAllocatedSim] = useState<INonAllocatedSimModel | null>(null)
  const {
    data: activeUnit,
    isLoading: activeIsLoading,
    isRefetching: activeIsRefetching
  } = useManagementGetUnitActive({
    config: {
      gcTime: 0,
      enabled: !isCreating
    },
    unitId: unitId || ''
  })

  const { isLoading: selectItemsLoading, companies, products, plans, firmwares, simApns, simTypes } = useManagementUnitSelectItems()
  const { mutate: createUnit, isPending: createIsPending } = useManagementCreateUnitActive(true, (unit) => doneCallback(unit))
  const { mutate: updateUnit, isPending: updateIsPending } = useManagementUpdateUnitActive(true, (unit) => doneCallback(unit))

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    setValue,
    reset
  } = useForm<IUnitSimActiveModel>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    if (!selectItemsLoading && !unitId) {
      setValue('productId', products.defaultValue)
      setValue('unitPlanId', plans.defaultValue)
      setValue('unitFirmwareId', firmwares.defaultValue)
      setValue('simApnId', simApns.defaultValue)
      setValue('simTypeId', simTypes.defaultValue)
    }
  }, [selectItemsLoading])

  useEffect(() => {
    if (activeUnit) {
      reset(activeUnit)
    }
  }, [activeUnit])

  useEffect(() => {
    if (isCreating) {
      if (nonAllocatedSim) {
        setValue('mobileNo', nonAllocatedSim.mobileNo)
        setValue('simNo', nonAllocatedSim.simNo)
        setValue('ip', nonAllocatedSim.ip)
      } else {
        setValue('mobileNo', '')
        setValue('simNo', '')
        setValue('ip', '')
      }
    }
  }, [nonAllocatedSim, isCreating])

  const submit = (values: IUnitSimActiveModel) => {
    if (isCreating) {
      createUnit({ model: values, nonAllocatedSimId: nonAllocatedSim?.id || 0 })
    } else {
      updateUnit({ model: values })
    }
  }

  const isLoading = selectItemsLoading || activeIsLoading || activeIsRefetching
  const isUpdating = isSubmitting || createIsPending || updateIsPending

  return (
    <div>
      <form noValidate onSubmit={handleSubmit(submit)}>
        <div className='grid md:grid-cols-2 gap-[20px] max-w-[1200px]'>
          <div className='flex flex-col gap-[20px]'>
            <CardContainer bgClasses='bg-surface border border-border'>
              <SectionTitle text='Company' classes='ml-[10px] text-text' />
              <VertSpacerLg />
              <div className='grid lg:grid-cols-2 gap-x-[20px]'>
                <SelectInputSM
                  type='text'
                  placeholder='Select Monitoring Centre'
                  label='Monitoring Centre'
                  id='monitoringCentreId'
                  showLabel
                  loading={isLoading}
                  disabled={isUpdating}
                  defaultValue={companies.defaultValue}
                  registration={register('monitoringCentreId', {
                    required: {
                      value: true,
                      message: 'Required'
                    }
                  })}
                  options={companies.options}
                  errors={errors.monitoringCentreId}
                />
                <SelectInputSM
                  type='text'
                  placeholder='Select Bureau'
                  label='Bureau'
                  id='bureauId'
                  showLabel
                  loading={isLoading}
                  disabled={isUpdating}
                  defaultValue={companies.defaultValue}
                  registration={register('bureauId', {
                    required: {
                      value: true,
                      message: 'Required'
                    }
                  })}
                  options={companies.options}
                  errors={errors.bureauId}
                />
                <SelectInputSM
                  type='text'
                  placeholder='Select Technician'
                  label='Technician'
                  id='techCompanyId'
                  showLabel
                  loading={isLoading}
                  disabled={isUpdating}
                  defaultValue={companies.defaultValue}
                  registration={register('techCompanyId')}
                  options={companies.options}
                  errors={errors.techCompanyId}
                />
                <CheckboxInputSM
                  label='Invoice on activation'
                  id='hardwareToBeInvoiced'
                  loading={isLoading}
                  disabled={isUpdating}
                  registration={register('hardwareToBeInvoiced')}
                  errors={errors.hardwareToBeInvoiced}
                />
              </div>
            </CardContainer>
            <CardContainer bgClasses='bg-surface border border-border'>
              <SectionTitle text='Device' classes='ml-[10px] text-text' />
              <VertSpacerLg />
              <div className='grid lg:grid-cols-2 gap-x-[20px]'>
                <SelectInputSM
                  type='text'
                  placeholder='Select Product'
                  label='Product'
                  id='productId'
                  showLabel
                  loading={isLoading}
                  disabled={isUpdating}
                  defaultValue={products.defaultValue}
                  registration={register('productId', {
                    required: {
                      value: true,
                      message: 'Required'
                    }
                  })}
                  options={products.options}
                  errors={errors.productId}
                />
                <SelectInputSM
                  type='text'
                  placeholder='Select Plan'
                  label='Plan'
                  id='unitPlanId'
                  showLabel
                  loading={isLoading}
                  disabled={isUpdating}
                  defaultValue={plans.defaultValue}
                  registration={register('unitPlanId', {
                    required: {
                      value: true,
                      message: 'Required'
                    }
                  })}
                  options={plans.options}
                  errors={errors.unitPlanId}
                />
                <SelectInputSM
                  type='text'
                  placeholder='Select Firmware'
                  label='Plan'
                  id='unitFirmwareId'
                  showLabel
                  loading={isLoading}
                  disabled={isUpdating}
                  defaultValue={firmwares.defaultValue}
                  registration={register('unitFirmwareId', {
                    required: {
                      value: true,
                      message: 'Required'
                    }
                  })}
                  options={firmwares.options}
                  errors={errors.unitFirmwareId}
                />
                <InputSM
                  type='text'
                  placeholder='CID account number'
                  label='CID account'
                  id='accountNo'
                  showLabel
                  loading={isLoading}
                  disabled={isUpdating}
                  registration={register('accountNo', {
                    required: {
                      value: true,
                      message: 'Required'
                    },
                    pattern: {
                      value: CID_ACCOUNT_REGEX,
                      message: '4 numerical characters only e.g. 0123'
                    }
                  })}
                  errors={errors.accountNo}
                />
                <InputSM
                  type='text'
                  placeholder='Device IMEI number'
                  label='IMEI number'
                  id='imei'
                  showLabel
                  loading={isLoading}
                  disabled={isUpdating}
                  registration={register('imei', {
                    required: {
                      value: true,
                      message: 'Required'
                    },
                    pattern: {
                      value: /^[0-9]+$/,
                      message: 'Numerical characters only'
                    }
                  })}
                  errors={errors.imei}
                />
                <InputSM
                  type='text'
                  placeholder='Device serial number'
                  label='Serial number'
                  id='serialNo'
                  showLabel
                  loading={isLoading}
                  disabled={isUpdating}
                  registration={register('serialNo', {
                    required: {
                      value: true,
                      message: 'Required'
                    },
                    pattern: {
                      value: /^[0-9]+$/,
                      message: 'Numerical characters only'
                    }
                  })}
                  errors={errors.serialNo}
                />
                <CheckboxInputSM
                  label='Activated'
                  id='isActivated'
                  loading={isLoading}
                  disabled={isUpdating}
                  registration={register('isActivated')}
                  errors={errors.isActivated}
                />
                <CheckboxInputSM
                  label='Line supervision'
                  id='lineSupervision'
                  loading={isLoading}
                  disabled={isUpdating}
                  registration={register('lineSupervision')}
                  errors={errors.lineSupervision}
                />
              </div>
            </CardContainer>
          </div>
          <div>
            <CardContainer bgClasses='bg-surface border border-border'>
              <div className='flex items-center justify-between'>
                <SectionTitle text='Sim Card' classes='ml-[10px] text-text' />
                {isCreating && (
                  <ModalButton
                    component={NonAllocatedSimSelectModal}
                    disabled={isLoading || isUpdating || nonAllocatedSim !== null}
                    classes='action-brand rounded-3xl flex items-center justify-between gap-[5px] px-4 py-1 jost-light'
                    confirmCallback={(sim: INonAllocatedSimModel) => setNonAllocatedSim(sim)}
                  >
                    <commonIcons.sim /> Non allocated sim
                  </ModalButton>
                )}
              </div>
              <VertSpacerLg />
              <div className='grid lg:grid-cols-2 gap-x-[20px]'>
                <SelectInputSM
                  type='text'
                  placeholder='Select APN'
                  label='APN'
                  id='simApnId'
                  showLabel
                  loading={isLoading}
                  disabled={isUpdating}
                  defaultValue={simApns.defaultValue}
                  registration={register('simApnId', {
                    required: {
                      value: true,
                      message: 'Required'
                    }
                  })}
                  options={simApns.options}
                  errors={errors.simApnId}
                />
                <SelectInputSM
                  type='text'
                  placeholder='Select Sim Type'
                  label='Sim Type'
                  id='simTypeId'
                  showLabel
                  loading={isLoading}
                  disabled={isUpdating}
                  defaultValue={simApns.defaultValue}
                  registration={register('simTypeId', {
                    required: {
                      value: true,
                      message: 'Required'
                    }
                  })}
                  options={simTypes.options}
                  errors={errors.simTypeId}
                />
              </div>
              {!nonAllocatedSim && (
                <>
                  <InputSM
                    type='text'
                    placeholder='Sim mobile number'
                    label='Mobile number'
                    id='mobileNo'
                    showLabel
                    loading={isLoading}
                    disabled={isUpdating}
                    registration={register('mobileNo', {
                      required: {
                        value: true,
                        message: 'Required'
                      },
                      pattern: {
                        value: /^[0-9]+$/,
                        message: 'Numerical characters only'
                      }
                    })}
                    errors={errors.mobileNo}
                  />
                  <InputSM
                    type='text'
                    placeholder='Sim card number'
                    label='Sim card number'
                    id='simNo'
                    showLabel
                    loading={isLoading}
                    disabled={isUpdating}
                    registration={register('simNo', {
                      required: {
                        value: true,
                        message: 'Required'
                      },
                      pattern: {
                        value: NUMBER_ONLY_REGEX,
                        message: 'Numerical characters only'
                      }
                    })}
                    errors={errors.simNo}
                  />
                  <InputSM
                    type='text'
                    placeholder='Sim card IP address'
                    label='Sim IP address'
                    id='ip'
                    showLabel
                    loading={isLoading}
                    disabled={isUpdating}
                    registration={register('ip', {
                      required: {
                        value: true,
                        message: 'Required'
                      },
                      pattern: {
                        value: IPV4_VALIDATION_REGEX,
                        message: 'Invalid IPv4 address'
                      }
                    })}
                    errors={errors.ip}
                  />
                </>
              )}
              {nonAllocatedSim && (
                <div className='border border-border p-2 rounded-3xl mt-1 mb-2'>
                  <div className='flex items-center justify-between'>
                    <div className='text-[0.8em] mb-2 bg-surface-2 text-text border border-border inline-block px-[7.5px] py-[2px] rounded-xl'>
                      From non-allocated sim
                    </div>
                    <button
                      type='button'
                      aria-label='Remove non-allocated sim'
                      className='action-warning'
                      onClick={() => setNonAllocatedSim(null)}
                    >
                      <commonIcons.close className='text-[1.45em]' />
                    </button>
                  </div>
                  <InputInfoSM id='mobileNo' showLabel label='Mobile number' value={nonAllocatedSim.mobileNo} className='!bg-surface-2' />
                  <InputInfoSM id='simNo' showLabel label='Sim card number' value={nonAllocatedSim.simNo} className='!bg-surface-2' />
                  <InputInfoSM id='ip' showLabel label='Sim IP address' value={nonAllocatedSim.ip} className='!bg-surface-2' />
                </div>
              )}
            </CardContainer>
          </div>
        </div>
        <VertSpacerLg />
        <ButtonRoundedSm
          type='submit'
          loading={isLoading || isUpdating}
          text={isCreating ? 'Create' : 'Update'}
          className='w-full sm:w-auto action-success'
        />
      </form>
    </div>
  )
}
